import React, { useEffect, useState } from 'react'
import { Crosshair, Eye } from 'lucide-react'
import { LengthUnits, LogoPlacement, lengthStep, roundLength } from '../types'
import { prepareLogoArtwork } from '../utils/patternTexture'
import { Checkbox, Field, NumberInput, Section, Select, Slider, TextInput, ToolButton } from './ui'

interface LogoPanelProps {
  placement: LogoPlacement
  onChange: (placement: LogoPlacement) => void
  /** Current part name — a logo is meant to be named (e.g. `logo_main`) for downstream logic. */
  name: string
  onRename: (name: string) => void
  /** Ids of the parts a logo can be wrapped onto (logo parts excluded). */
  targetIds: string[]
  /** Display unit, and the factor from model units into it. */
  units: LengthUnits
  /** Click-on-model placement: pick the spot instead of typing an angle. */
  pickMode: boolean
  onTogglePick: () => void
  /** Aim the camera square on at the logo, the only view that shows it undistorted. */
  onFace: () => void
  /** How far the logo travels around the part, in degrees. 0 when it is not wrapped. */
  wrapDeg: number
}

const num = (raw: string, fallback: number) => {
  const v = parseFloat(raw)
  return Number.isFinite(v) ? v : fallback
}

const round1 = (v: number) => Math.round(v * 10) / 10

const DEG_TO_RAD = Math.PI / 180

/**
 * Placement controls for a logo PART (a real mesh on the model), as opposed to the
 * Pattern controls, which print artwork into the body's own texture. Every length is
 * shown in the display unit; the placement itself is stored in model units.
 */
export const LogoPanel: React.FC<LogoPanelProps> = ({
  placement,
  onChange,
  name,
  onRename,
  targetIds,
  units,
  pickMode,
  onTogglePick,
  onFace,
  wrapDeg,
}) => {
  // Renaming is committed on Enter/blur: the id is the part's identity, so renaming on
  // every keystroke would churn the selection and the undo history.
  const [nameDraft, setNameDraft] = useState(name)
  useEffect(() => setNameDraft(name), [name])
  const commitName = () => {
    const trimmed = nameDraft.trim()
    if (trimmed && trimmed !== name) onRename(trimmed)
    else setNameDraft(name)
  }

  const { unit, scale } = units
  // Model units → display units for a field, and a typed display value → model units.
  const show = (v: number) => roundLength(v * scale, unit)
  const showFine = (v: number) => roundLength(v * scale, unit, 1)
  const typed = (raw: string, current: number) => num(raw, current * scale) / (scale || 1)

  const set = <K extends keyof LogoPlacement>(key: K, value: LogoPlacement[K]) =>
    onChange({ ...placement, [key]: value })

  const aspect = placement.imageAspect && placement.imageAspect > 0 ? placement.imageAspect : null

  // Resizing keeps the artwork's proportions — a stretched logo is almost never wanted.
  const setWidth = (width: number) => {
    onChange({ ...placement, width, height: aspect ? width / aspect : placement.height })
  }
  const setHeight = (height: number) => {
    onChange({ ...placement, height, width: aspect ? height * aspect : placement.width })
  }

  // How far the wrap pulls the logo's ends away from the viewer, as a share of the
  // radius. This is what makes a wide logo read as curved.
  const setback = Math.round(100 * (1 - Math.cos((wrapDeg / 2) * DEG_TO_RAD)))

  const handleImage = (file: File | undefined) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = async () => {
      const src = reader.result as string
      try {
        const artwork = await prepareLogoArtwork(src)
        // Height is the dimension a logo is normally specified by, so it is the one held
        // steady across a swap. Holding width instead would blow a tall, narrow mark up
        // to many times the height of the product.
        onChange({
          ...placement,
          image: artwork.image,
          imageAspect: artwork.aspect,
          width: placement.height * artwork.aspect,
          knockoutWhite: artwork.knockoutWhite,
        })
      } catch {
        // Unreadable image — leave the current artwork in place.
      }
    }
    reader.readAsDataURL(file)
  }

  return (
    <Section
      title="Logo placement"
      action={
        <div className="flex gap-xs">
          <ToolButton onClick={onFace} icon={<Eye size={13} />} title="Look straight at the logo">
            Face it
          </ToolButton>
          <ToolButton
            onClick={onTogglePick}
            active={pickMode}
            icon={<Crosshair size={13} />}
            title="Click a spot on the model to move the logo there"
          >
            {pickMode ? 'Click…' : 'Pick'}
          </ToolButton>
        </div>
      }
    >
      {/* Name — this is what downstream logic keys off (e.g. logo_main). */}
      <Field label="Name">
        <TextInput
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitName()
            else if (e.key === 'Escape') setNameDraft(name)
          }}
          className="font-mono"
        />
      </Field>

      <Field label="Artwork">
        <div className="flex items-center gap-sm">
          <img
            src={placement.image}
            alt="logo"
            className="w-[44px] h-[44px] rounded-md object-contain bg-gray-100 border border-gray-200 flex-shrink-0"
          />
          <label className="flex-1 h-[37px] flex items-center justify-center bg-white border border-gray-300 hover:border-primary-500 text-gray-700 rounded-md text-text-md cursor-pointer transition-all">
            Replace artwork
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} />
          </label>
        </div>
      </Field>

      <div className="flex gap-sm">
        <Field label={`Width (${unit})`} className="flex-1">
          <NumberInput
            step={lengthStep(unit)}
            value={show(placement.width)}
            onChange={(e) => setWidth(typed(e.target.value, placement.width))}
          />
        </Field>
        <Field label={`Height (${unit})`} className="flex-1">
          <NumberInput
            step={lengthStep(unit)}
            value={show(placement.height)}
            onChange={(e) => setHeight(typed(e.target.value, placement.height))}
          />
        </Field>
      </div>
      {aspect && (
        <p className="text-text-xs text-gray-400 -mt-xs mb-sm">
          Proportions locked to the artwork. Sizes are the visible mark — empty margins in the file are trimmed off.
        </p>
      )}

      <Field label="Around the model — 0° is the front" value={`${round1(placement.angleDeg)}°`}>
        <Slider
          min="-180"
          max="180"
          step="0.5"
          value={placement.angleDeg}
          onChange={(e) => set('angleDeg', parseFloat(e.target.value))}
        />
      </Field>

      <div className="flex gap-sm">
        <Field label={`Height on model (${unit})`} className="flex-1">
          <NumberInput
            step={lengthStep(unit)}
            value={show(placement.centerY)}
            onChange={(e) => set('centerY', typed(e.target.value, placement.centerY))}
          />
        </Field>
        <Field label="Rotate (°)" className="flex-1">
          <NumberInput
            step="1"
            value={round1(placement.rotationDeg)}
            onChange={(e) => set('rotationDeg', num(e.target.value, placement.rotationDeg))}
          />
        </Field>
      </div>

      <div className="flex gap-sm">
        <Field label={`Lift off surface (${unit})`} className="flex-1">
          <NumberInput
            step={lengthStep(unit, true)}
            value={showFine(placement.standoff)}
            onChange={(e) => set('standoff', typed(e.target.value, placement.standoff))}
          />
        </Field>
        <Field label={`Emboss (${unit})`} className="flex-1">
          <NumberInput
            min="0"
            step={lengthStep(unit, true)}
            value={showFine(placement.thickness)}
            onChange={(e) => set('thickness', Math.max(0, typed(e.target.value, placement.thickness)))}
          />
        </Field>
      </div>

      <Field label="Wrap onto">
        <Select
          value={placement.targetId ?? ''}
          onChange={(e) => set('targetId', e.target.value || undefined)}
          className="font-mono"
        >
          <option value="">Largest part (auto)</option>
          {targetIds.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </Select>
      </Field>

      <Checkbox
        checked={placement.conform}
        onChange={(v) => set('conform', v)}
        label="Follow the surface (curved)"
        className="mb-xs"
      />
      <Checkbox
        checked={placement.knockoutWhite ?? false}
        onChange={(v) => set('knockoutWhite', v)}
        label="Cut out white background"
        className="mb-xs"
      />
      <Checkbox
        checked={placement.cutToShape ?? false}
        onChange={(v) => set('cutToShape', v)}
        label="Cut to artwork shape"
      />
      <p className="text-text-xs text-gray-400 mt-xs">
        Cut to shape makes the part the logo&apos;s own outline instead of a rectangle showing its shape through
        transparency — so it still reads as the logo in Blender&apos;s solid view, in the old customizer, and anywhere
        else that ignores alpha.
      </p>

      {wrapDeg > 0 && (
        <p className={`text-text-xs mt-sm ${wrapDeg > 45 ? 'text-warning-700' : 'text-gray-400'}`}>
          Wraps {Math.round(wrapDeg)}° of the part — its ends sit {setback}% of the radius further back than its middle.
          {wrapDeg > 45 && ' That much curve reads as a lean from any raised viewpoint; narrow it to flatten it.'}
        </p>
      )}
      <p className="text-text-xs text-gray-400 mt-sm">
        Judge the placement with <span className="text-gray-600">Face it</span>. From any other angle a wrapped logo
        leans, because its ends sit further from the eye than its middle.
      </p>
      <p className="text-text-xs text-gray-400 mt-xs">
        The logo is a real part: it shows in the outliner, keeps its name in the exported GLB, and its transparent areas
        are cut away. Emboss above 0 makes it a solid raised badge.
      </p>
    </Section>
  )
}
